import { ModalId } from '@heidi/schema/ml-scribe';

import { setOverride } from '@/features/flags-dev/overrides';
import { forceShowModalLocal } from '@/lib/modal-debugger';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { Sparkles } from 'lucide-react';
import { useCallback } from 'react';

/** Flagsmith flag that mounts the Patients intro card in `GlobalModalsWrapper`. */
const PATIENT_LIST_FLAG_KEY = 'release-scribe-enable-patient-list';

interface PatientListIntroDevSectionProps {
  /** Called after the user triggers an action that should close the dev-tools sheet. */
  onRequestClose?: () => void;
}

/**
 * Dev-tools shortcut for the Patients GA awareness card
 * ({@link ModalId.PATIENT_LIST_INTRODUCTION}). One click flips the
 * `release-scribe-enable-patient-list` override on (so the card is mounted in
 * `GlobalModalsWrapper`) and force-shows it into the queue at CRITICAL
 * priority. The override re-emits OpenFeature `ConfigurationChanged`, so the
 * gate re-evaluates live — no reload needed.
 *
 * The generic per-modal "Force show" in the Seen modals section below does not
 * touch the feature flag, so it can't surface this card on its own when the
 * flag is off; this section exists to bridge that gap.
 */
export function PatientListIntroDevSection({
  onRequestClose,
}: Readonly<PatientListIntroDevSectionProps>) {
  const handleShow = useCallback(() => {
    setOverride('flagsmith', PATIENT_LIST_FLAG_KEY, 'on');
    forceShowModalLocal(ModalId.PATIENT_LIST_INTRODUCTION);
    onRequestClose?.();
  }, [onRequestClose]);

  return (
    <section aria-labelledby="dev-tools-patient-list-intro-heading" className="mb-6">
      <TypographyP2SemiBold id="dev-tools-patient-list-intro-heading" className="mb-2">
        Patients announcement card
      </TypographyP2SemiBold>
      <TypographyCaption className="mb-3 text-text-tertiary">
        Enables the <span className="font-mono">{PATIENT_LIST_FLAG_KEY}</span> override
        and force-shows the bottom-left awareness card. Optimistic-only — clears on the
        next user refetch. Figma: <span className="font-mono">2833:167881</span>.
      </TypographyCaption>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        onClick={handleShow}
        data-testid="dev-tools-patient-list-intro-show"
      >
        <Sparkles size={14} aria-hidden /> Show Patients card
      </Button>
    </section>
  );
}
